import type {SendAnalyticsEventFunction} from '@github-ui/use-analytics'
import {
  createSafeIndexedDbPersister,
  type CacheEvent,
  type ANY_CACHE_EVENT,
  type NO_CACHE_EVENTS,
  CACHE_EVENTS,
} from '@github-ui/safe-indexed-db-storage'
import {getCurrentUserLogin} from '@github-ui/client-env'
import type {CachedQuery} from './types'
import type {GraphQLResult} from '@github-ui/fetch-graphql'
import {createQueryCache, type QueryCache, generateCacheKey, retrieveQueryFromHotCache} from './create-query-cache'
export {CACHE_EVENTS} from '@github-ui/safe-indexed-db-storage'
export {INTERNAL_ONLY_ATTRIBUTES} from './create-query-cache'
export {generateCacheKey, retrieveQueryFromHotCache}

const DEFAULT_VALIDATOR = {
  Check: (value: unknown): value is GraphQLResult => {
    if (typeof value !== 'object' || value === null) return false
    const data = value as {data?: unknown; errors?: unknown}
    if (data.data !== undefined && typeof data.data !== 'object') return false
    if (data.errors !== undefined && !Array.isArray(data.errors)) return false
    return true
  },
  Code: () => 'manual-validator',
  Errors: () => [],
}

export const PERSISTER_KEY_PREFIX = 'relay-query'
const ONE_WEEK = 1000 * 60 * 60 * 24 * 7

type SpecificCacheEvent = Exclude<CacheEvent, ANY_CACHE_EVENT | NO_CACHE_EVENTS>

type Props = {
  sendAnalyticsEvent: SendAnalyticsEventFunction
  reportableEvents?: ANY_CACHE_EVENT | NO_CACHE_EVENTS | SpecificCacheEvent[]
  maxAgeMs?: number
}

export const QUERY_CACHE_VERSION = '1.0.8'

export type {QueryCache}
export const createCache = <T>({
  sendAnalyticsEvent,
  reportableEvents = CACHE_EVENTS.ANY,
  maxAgeMs = ONE_WEEK,
}: Props): QueryCache<T> => {
  const storage = createSafeIndexedDbPersister<{data: T | null}, CachedQuery<T>>({
    validator: DEFAULT_VALIDATOR,
    sendAnalyticsEvent: (eventType: string, ...rest) => {
      if (reportableEvents === CACHE_EVENTS.NONE) return
      if (
        reportableEvents === CACHE_EVENTS.ANY ||
        (Array.isArray(reportableEvents) && reportableEvents.includes(eventType as SpecificCacheEvent))
      ) {
        sendAnalyticsEvent(eventType, ...rest)
      }
    },
  })
  return createQueryCache<T>({
    storage,
    prefix: `${PERSISTER_KEY_PREFIX}-${getCurrentUserLogin()}`,
    maxAgeMs,
    sendAnalyticsEvent,
  })
}
